// RigSentinel Calc - Subsea Stack Well Control calculators.
// All formulas are deterministic. The category-level safety banner + the
// per-calc subsea banner are wired in catalog.js via `wellControl: true` and
// `stackMode: "subsea"`. Calculators here are intentionally minimal — they
// compute capacities and totals from rig-specific data the user enters or
// pulls from the Subsea Well Control Profile.

import { round, num, positive } from "./_common";

const CAP = (id_in, length_ft) => (id_in * id_in * length_ft) / 1029.4;

// 1) Marine Riser Capacity
//    C_riser (bbl) = ID² × L / 1029.4
export function marineRiserCapacity({ riserId, riserLength }) {
  const ID = positive(num(riserId, "Riser ID"), "Riser ID");
  const L  = positive(num(riserLength, "Riser Length"), "Riser Length");
  const v = CAP(ID, L);
  return {
    result: round(v, 2),
    unit: "bbl",
    formula: "C_riser = ID² × L / 1029.4",
    steps: [
      `ID = ${ID} in,  L = ${L} ft`,
      `C_riser = ${ID}² × ${L} / 1029.4`,
      `C_riser = ${round(v, 2)} bbl`,
    ],
    inputs: { "Riser ID (in)": ID, "Riser Length (ft)": L },
    extras: { "Riser capacity (gal)": round(v * 42, 1) },
  };
}

// 2) Choke Line Capacity
//    C_choke (bbl) = ID² × L / 1029.4
export function chokeLineCapacity({ chokeLineId, chokeLineLength }) {
  const ID = positive(num(chokeLineId, "Choke Line ID"), "Choke Line ID");
  const L  = positive(num(chokeLineLength, "Choke Line Length"), "Choke Line Length");
  const v = CAP(ID, L);
  return {
    result: round(v, 2),
    unit: "bbl",
    formula: "C_choke = ID² × L / 1029.4",
    steps: [
      `ID = ${ID} in,  L = ${L} ft`,
      `C_choke = ${ID}² × ${L} / 1029.4`,
      `C_choke = ${round(v, 2)} bbl`,
    ],
    inputs: { "Choke Line ID (in)": ID, "Choke Line Length (ft)": L },
  };
}

// 3) Kill Line Capacity
//    C_kill (bbl) = ID² × L / 1029.4
export function killLineCapacity({ killLineId, killLineLength }) {
  const ID = positive(num(killLineId, "Kill Line ID"), "Kill Line ID");
  const L  = positive(num(killLineLength, "Kill Line Length"), "Kill Line Length");
  const v = CAP(ID, L);
  return {
    result: round(v, 2),
    unit: "bbl",
    formula: "C_kill = ID² × L / 1029.4",
    steps: [
      `ID = ${ID} in,  L = ${L} ft`,
      `C_kill = ${ID}² × ${L} / 1029.4`,
      `C_kill = ${round(v, 2)} bbl`,
    ],
    inputs: { "Kill Line ID (in)": ID, "Kill Line Length (ft)": L },
  };
}

// 4) Total Choke and Kill Line Volume
//    V_ck = V_choke + V_kill
export function totalChokeKillVolume({ chokeVolume, killVolume }) {
  const C = positive(num(chokeVolume, "Choke Line Volume"), "Choke Line Volume");
  const K = positive(num(killVolume, "Kill Line Volume"), "Kill Line Volume");
  const v = C + K;
  return {
    result: round(v, 2),
    unit: "bbl",
    formula: "V_C/K = V_choke + V_kill",
    steps: [
      `V_choke = ${C} bbl,  V_kill = ${K} bbl`,
      `V_C/K = ${C} + ${K} = ${round(v, 2)} bbl`,
    ],
    inputs: { "Choke Line Volume (bbl)": C, "Kill Line Volume (bbl)": K },
  };
}

// 5) Riser Circulation Volume
//    Equal to the marine-riser internal capacity (carry-through helper).
export function riserCirculationVolume({ riserCapacity }) {
  const V = positive(num(riserCapacity, "Riser Capacity"), "Riser Capacity");
  return {
    result: round(V, 2),
    unit: "bbl",
    formula: "V_riser_circ = C_riser",
    steps: [
      `C_riser = ${V} bbl`,
      `V_riser_circ = ${round(V, 2)} bbl`,
    ],
    inputs: { "Riser Capacity (bbl)": V },
  };
}

// =========================================================================
// STAGE 2 — Riser & seawater hydrostatics, differential & riser-margin.
// =========================================================================

// 6) Riser Hydrostatic Pressure: P = 0.052 × MW × TVD_riser
export function riserHydrostaticPressure({ mw, riserTvd }) {
  const M = positive(num(mw, "Mud Weight"), "Mud Weight");
  const D = positive(num(riserTvd, "Riser TVD"), "Riser TVD");
  const p = 0.052 * M * D;
  return {
    result: round(p, 2),
    unit: "psi",
    formula: "P_riser = 0.052 × MW × TVD_riser",
    steps: [
      `MW = ${M} ppg,  TVD_riser = ${D} ft`,
      `P_riser = 0.052 × ${M} × ${D}`,
      `P_riser = ${round(p, 2)} psi`,
    ],
    inputs: { "Mud Weight (ppg)": M, "Riser TVD (ft)": D },
  };
}

// 7) Seawater Hydrostatic Pressure: P_sw = 0.445 × WD
//    0.445 psi/ft is a typical seawater gradient estimate — verify against
//    site-specific seawater density / temperature for operational use.
export function seawaterHydrostaticPressure({ waterDepth }) {
  const D = positive(num(waterDepth, "Water Depth"), "Water Depth");
  const p = 0.445 * D;
  return {
    result: round(p, 2),
    unit: "psi",
    formula: "P_sw = 0.445 × Water Depth   (typical seawater gradient — verify)",
    steps: [
      `Water Depth = ${D} ft`,
      `P_sw = 0.445 × ${D}`,
      `P_sw = ${round(p, 2)} psi`,
    ],
    inputs: { "Water Depth (ft)": D },
    extras: {
      "Gradient used (psi/ft)": 0.445,
      "Note": "0.445 psi/ft is a typical seawater gradient estimate. Verify against site-specific seawater density and temperature before operational use.",
    },
    worksheetWarning: true,
  };
}

// 8) Differential Riser Hydrostatic Pressure: ΔP = P_mud − P_sw
export function differentialRiserHydrostatic({ mudHydrostatic, seawaterHydrostatic }) {
  const M = num(mudHydrostatic, "Mud Hydrostatic Pressure");
  const S = num(seawaterHydrostatic, "Seawater Hydrostatic Pressure");
  if (M < 0) throw new Error("Mud hydrostatic pressure must be ≥ 0");
  if (S < 0) throw new Error("Seawater hydrostatic pressure must be ≥ 0");
  const d = M - S;
  return {
    result: round(d, 2),
    unit: "psi",
    formula: "ΔP = P_mud − P_sw",
    steps: [
      `P_mud = ${M} psi,  P_sw = ${S} psi`,
      `ΔP = ${M} − ${S} = ${round(d, 2)} psi`,
    ],
    inputs: { "Mud Hydrostatic (psi)": M, "Seawater Hydrostatic (psi)": S },
  };
}

// 9) Riser Margin Pressure: P_riser_margin = 0.052 × ΔMW × WD
export function riserMarginPressure({ riserMarginPpg, waterDepth }) {
  const M = positive(num(riserMarginPpg, "Riser Margin"), "Riser Margin");
  const D = positive(num(waterDepth, "Water Depth"), "Water Depth");
  const p = 0.052 * M * D;
  return {
    result: round(p, 2),
    unit: "psi",
    formula: "P_riser_margin = 0.052 × Riser Margin (ppg) × Water Depth",
    steps: [
      `Riser Margin = ${M} ppg,  Water Depth = ${D} ft`,
      `P = 0.052 × ${M} × ${D}`,
      `P = ${round(p, 2)} psi`,
    ],
    inputs: { "Riser Margin (ppg)": M, "Water Depth (ft)": D },
  };
}

// 10) Required Riser Margin Mud Weight:
//     ΔMW (ppg) = Desired Pressure Margin / (0.052 × Water Depth)
export function requiredRiserMarginMW({ desiredPressure, waterDepth }) {
  const P = positive(num(desiredPressure, "Desired Pressure Margin"), "Desired Pressure Margin");
  const D = positive(num(waterDepth, "Water Depth"), "Water Depth");
  const dMW = P / (0.052 * D);
  return {
    result: round(dMW, 2),
    unit: "ppg",
    formula: "Required Riser Margin (ppg) = Desired ΔP / (0.052 × Water Depth)",
    steps: [
      `Desired ΔP = ${P} psi,  Water Depth = ${D} ft`,
      `ΔMW = ${P} / (0.052 × ${D})`,
      `ΔMW = ${round(dMW, 2)} ppg`,
    ],
    inputs: { "Desired ΔP (psi)": P, "Water Depth (ft)": D },
  };
}


// =========================================================================
// STAGE 3 — Choke/Kill line friction support & subsea ICP/FCP support.
// • Friction "Record" tools are intentionally NOT prediction calculators —
//   they package a measured value and the test conditions for documentation.
// • ICP/FCP support tools apply the simplest classroom subsea formulation
//   (add choke-line friction). They are worksheet support only; company
//   procedure may handle CLF differently.
// =========================================================================

// 11) Choke Line Friction RECORD (worksheet, not a prediction).
export function chokeLineFrictionRecord({ pumpRate, mw, chokeLineId, chokeLineLength, measuredFriction, testDateTime, notes }) {
  const Q  = positive(num(pumpRate, "Pump Rate"), "Pump Rate");
  const M  = positive(num(mw, "Mud Weight"), "Mud Weight");
  const ID = positive(num(chokeLineId, "Choke Line ID"), "Choke Line ID");
  const L  = positive(num(chokeLineLength, "Choke Line Length"), "Choke Line Length");
  const P  = num(measuredFriction, "Measured Friction Pressure");
  if (P < 0) throw new Error("Measured friction pressure must be ≥ 0");
  const when = (typeof testDateTime === "string" ? testDateTime.trim() : "") || "—";
  const note = (typeof notes === "string" ? notes.trim() : "") || "—";
  return {
    result: round(P, 2),
    unit: "psi (measured — record only)",
    formula: "Record only — measured choke-line friction at the stated test conditions",
    steps: [
      `Pump Rate: ${Q} spm`,
      `Mud Weight: ${M} ppg`,
      `Choke Line ID: ${ID} in,  Length: ${L} ft`,
      `Measured Friction: ${P} psi`,
      `Test Date/Time: ${when}`,
      `Notes: ${note}`,
    ],
    inputs: {
      "Pump Rate (spm)": Q,
      "Mud Weight (ppg)": M,
      "Choke Line ID (in)": ID,
      "Choke Line Length (ft)": L,
      "Measured Friction (psi)": P,
      "Test Date/Time": when,
      "Notes": note,
    },
    extras: {
      "Status": "Record only — do NOT use as a prediction",
      "Note": "Do not predict choke-line friction unless a verified hydraulic model and rig-specific test data are used.",
    },
    worksheetWarning: true,
  };
}

// 12) Kill Line Friction RECORD (worksheet, not a prediction).
export function killLineFrictionRecord({ pumpRate, mw, killLineId, killLineLength, measuredFriction, testDateTime, notes }) {
  const Q  = positive(num(pumpRate, "Pump Rate"), "Pump Rate");
  const M  = positive(num(mw, "Mud Weight"), "Mud Weight");
  const ID = positive(num(killLineId, "Kill Line ID"), "Kill Line ID");
  const L  = positive(num(killLineLength, "Kill Line Length"), "Kill Line Length");
  const P  = num(measuredFriction, "Measured Friction Pressure");
  if (P < 0) throw new Error("Measured friction pressure must be ≥ 0");
  const when = (typeof testDateTime === "string" ? testDateTime.trim() : "") || "—";
  const note = (typeof notes === "string" ? notes.trim() : "") || "—";
  return {
    result: round(P, 2),
    unit: "psi (measured — record only)",
    formula: "Record only — measured kill-line friction at the stated test conditions",
    steps: [
      `Pump Rate: ${Q} spm`,
      `Mud Weight: ${M} ppg`,
      `Kill Line ID: ${ID} in,  Length: ${L} ft`,
      `Measured Friction: ${P} psi`,
      `Test Date/Time: ${when}`,
      `Notes: ${note}`,
    ],
    inputs: {
      "Pump Rate (spm)": Q,
      "Mud Weight (ppg)": M,
      "Kill Line ID (in)": ID,
      "Kill Line Length (ft)": L,
      "Measured Friction (psi)": P,
      "Test Date/Time": when,
      "Notes": note,
    },
    extras: {
      "Status": "Record only — do NOT use as a prediction",
      "Note": "Do not predict kill-line friction unless a verified hydraulic model and rig-specific test data are used.",
    },
    worksheetWarning: true,
  };
}

// 13) SCR with Choke Line Friction:  P_adj = SCR + CLF
export function scrWithChokeFriction({ scrPressure, chokeLineFriction }) {
  const S = positive(num(scrPressure, "SCR Pressure"), "SCR Pressure");
  const F = num(chokeLineFriction, "Choke Line Friction");
  if (F < 0) throw new Error("Choke line friction must be ≥ 0");
  const p = S + F;
  return {
    result: round(p, 2),
    unit: "psi",
    formula: "Adjusted Circulating Pressure = SCR + Choke Line Friction",
    steps: [
      `SCR = ${S} psi,  CLF = ${F} psi`,
      `P_adj = ${S} + ${F} = ${round(p, 2)} psi`,
    ],
    inputs: { "SCR (psi)": S, "Choke Line Friction (psi)": F },
    extras: {
      "Note": "Use only if this matches approved company subsea well-control procedure.",
    },
    worksheetWarning: true,
  };
}

// 14) Subsea ICP Support:  ICP = SIDPP + SCR + CLF
export function subseaIcpSupport({ sidpp, scrPressure, chokeLineFriction }) {
  const P = num(sidpp, "SIDPP"); if (P < 0) throw new Error("SIDPP must be ≥ 0");
  const S = positive(num(scrPressure, "SCR Pressure"), "SCR Pressure");
  const F = num(chokeLineFriction, "Choke Line Friction"); if (F < 0) throw new Error("Choke line friction must be ≥ 0");
  const icp = P + S + F;
  return {
    result: round(icp, 2),
    unit: "psi",
    formula: "Subsea ICP (support) = SIDPP + SCR + Choke Line Friction",
    steps: [
      `SIDPP = ${P} psi,  SCR = ${S} psi,  CLF = ${F} psi`,
      `ICP = ${P} + ${S} + ${F} = ${round(icp, 2)} psi`,
    ],
    inputs: { "SIDPP (psi)": P, "SCR (psi)": S, "Choke Line Friction (psi)": F },
    extras: {
      "Status": "Worksheet support only",
      "Note": "Company procedure may handle choke-line friction differently. Verify against approved subsea kill-sheet logic.",
    },
    worksheetWarning: true,
  };
}

// 15) Subsea FCP Support:  FCP = SCR × (KMW/OMW) + CLF
export function subseaFcpSupport({ scrPressure, killMW, originalMW, chokeLineFriction }) {
  const S = positive(num(scrPressure, "SCR Pressure"), "SCR Pressure");
  const K = positive(num(killMW, "Kill Mud Weight"), "Kill Mud Weight");
  const O = positive(num(originalMW, "Original Mud Weight"), "Original Mud Weight");
  const F = num(chokeLineFriction, "Choke Line Friction"); if (F < 0) throw new Error("Choke line friction must be ≥ 0");
  const fcp = S * (K / O) + F;
  return {
    result: round(fcp, 2),
    unit: "psi",
    formula: "Subsea FCP (support) = SCR × (KMW / OMW) + Choke Line Friction",
    steps: [
      `SCR = ${S} psi,  KMW = ${K} ppg,  OMW = ${O} ppg,  CLF = ${F} psi`,
      `Ratio KMW/OMW = ${round(K / O, 4)}`,
      `FCP = ${S} × ${round(K / O, 4)} + ${F} = ${round(fcp, 2)} psi`,
    ],
    inputs: { "SCR (psi)": S, "Kill MW (ppg)": K, "Original MW (ppg)": O, "Choke Line Friction (psi)": F },
    extras: {
      "Status": "Worksheet support only",
      "Note": "Company procedure may handle choke-line friction differently. Verify against approved subsea kill-sheet logic.",
    },
    worksheetWarning: true,
  };
}


// =========================================================================
// STAGE 4 — Subsea Kill Sheet Worksheet Support
// One large draft worksheet. NOT a live kill-procedure assistant.
// Optional inputs: empty strings are treated as missing; derived outputs are
// only produced where their required inputs are present.
// =========================================================================

const _DRAFT_HEADER = "DRAFT WORKSHEET — Certified review only.";
const _KILL_WARNING_LINES = [
  "1. This worksheet is for calculation support, documentation, training, and qualified review only.",
  "2. It is not a live well-control instruction system.",
  "3. Final decisions must follow approved company procedures, rig-specific subsea data, certified well-control personnel, and regulatory requirements.",
  "4. Company procedures may handle choke line friction differently.",
];

// Read an optional numeric input. Treats "", null, undefined as missing.
// Returns null if missing or non-numeric so callers can branch on it.
function _opt(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// Read an optional non-empty string. Returns null if blank.
function _optStr(v) {
  if (typeof v !== "string") return null;
  const t = v.trim();
  return t.length > 0 ? t : null;
}

export function subseaKillSheetWorksheet(args) {
  // Strings
  const rigName             = _optStr(args.rigName);
  const wellName            = _optStr(args.wellName);
  const lotFitNotes         = _optStr(args.lotFitNotes);
  const procedureRef        = _optStr(args.procedureRef);
  const userNotes           = _optStr(args.userNotes);
  // Numbers
  const waterDepth          = _opt(args.waterDepth);
  const bopStackDepth       = _opt(args.bopStackDepth);
  const rkbToMudline        = _opt(args.rkbToMudline);
  const currentMW           = _opt(args.currentMW);
  const killMWInput         = _opt(args.killMW);          // may be optional override
  const sidpp               = _opt(args.sidpp);
  const sicp                = _opt(args.sicp);
  const pitGain             = _opt(args.pitGain);
  const tvd                 = _opt(args.tvd);
  const scrPressure         = _opt(args.scrPressure);
  const killRate            = _opt(args.killRate);
  const pumpOutput          = _opt(args.pumpOutput);
  const drillStringVol      = _opt(args.drillStringVol);
  const annularVol          = _opt(args.annularVol);
  const riserVol            = _opt(args.riserVol);
  const chokeLineVol        = _opt(args.chokeLineVol);
  const killLineVol         = _opt(args.killLineVol);
  const chokeFriction       = _opt(args.chokeFriction);
  const killFriction        = _opt(args.killFriction);
  const chokeFrictionRate   = _opt(args.chokeFrictionRate);
  const killFrictionRate    = _opt(args.killFrictionRate);
  const casingShoeTvd       = _opt(args.casingShoeTvd);
  const selectedVolume      = _opt(args.selectedVolume);  // for circulation time

  const missing = [];

  // --- Kill Mud Weight: KMW = CurrentMW + SIDPP / (0.052 × TVD) ---
  let kmw = null;
  if (currentMW != null && sidpp != null && tvd != null && tvd > 0) {
    kmw = round(currentMW + sidpp / (0.052 * tvd), 2);
  } else if (killMWInput != null) {
    kmw = round(killMWInput, 2);   // user-provided override
  } else {
    missing.push("Kill MW: need Current MW + SIDPP + TVD (or enter Kill MW directly)");
  }

  // --- Subsea ICP Support: SIDPP + SCR + CLF ---
  let icp = null;
  if (sidpp != null && scrPressure != null && chokeFriction != null) {
    icp = round(sidpp + scrPressure + chokeFriction, 2);
  } else {
    missing.push("Subsea ICP Support: need SIDPP + SCR + Choke Line Friction");
  }

  // --- Subsea FCP Support: SCR × (KMW / OMW) + CLF ---
  let fcp = null;
  if (scrPressure != null && kmw != null && currentMW != null && currentMW > 0 && chokeFriction != null) {
    fcp = round(scrPressure * (kmw / currentMW) + chokeFriction, 2);
  } else {
    missing.push("Subsea FCP Support: need SCR + Kill MW + Original MW + Choke Line Friction");
  }

  // --- Strokes to Bit = DrillStringVol / PumpOutput ---
  let strokesToBit = null;
  if (drillStringVol != null && drillStringVol > 0 && pumpOutput != null && pumpOutput > 0) {
    strokesToBit = Math.round(drillStringVol / pumpOutput);
  } else {
    missing.push("Strokes to Bit: need Drill String Internal Volume + Pump Output");
  }

  // --- Bottoms-Up Strokes = AnnularVol / PumpOutput ---
  let bottomsUp = null;
  if (annularVol != null && annularVol > 0 && pumpOutput != null && pumpOutput > 0) {
    bottomsUp = Math.round(annularVol / pumpOutput);
  } else {
    missing.push("Bottoms-Up Strokes: need Annular Volume + Pump Output");
  }

  // --- Pump rate (bbl/min) = pumpOutput × killRate (SPM) ---
  let pumpRateBblMin = null;
  if (pumpOutput != null && pumpOutput > 0 && killRate != null && killRate > 0) {
    pumpRateBblMin = round(pumpOutput * killRate, 3);
  }

  // --- Circulation Time (min) = SelectedVolume / pump rate ---
  let circulationTimeMin = null;
  if (selectedVolume != null && selectedVolume > 0 && pumpRateBblMin != null && pumpRateBblMin > 0) {
    circulationTimeMin = round(selectedVolume / pumpRateBblMin, 2);
  } else if (pumpRateBblMin == null) {
    missing.push("Circulation Time: need Pump Output + Kill Rate to compute pump rate (bbl/min)");
  } else {
    missing.push("Circulation Time: need a Selected Volume (bbl)");
  }

  // Carry-through volumes
  const riserVolOut       = riserVol       != null ? round(riserVol, 2)       : null;
  const chokeLineVolOut   = chokeLineVol   != null ? round(chokeLineVol, 2)   : null;
  const killLineVolOut    = killLineVol    != null ? round(killLineVol, 2)    : null;

  // -----------------------------------------------------------------------
  // Build the worksheet output
  // -----------------------------------------------------------------------
  const inputs = {};
  const setIn = (k, v) => { if (v !== null && v !== undefined && v !== "") inputs[k] = v; };
  setIn("Rig",                          rigName);
  setIn("Well",                         wellName);
  setIn("Water Depth (ft)",             waterDepth);
  setIn("BOP Stack Depth (ft)",         bopStackDepth);
  setIn("RKB → Mudline (ft)",           rkbToMudline);
  setIn("Current MW (ppg)",             currentMW);
  setIn("Kill MW input (ppg)",          killMWInput);
  setIn("SIDPP (psi)",                  sidpp);
  setIn("SICP (psi)",                   sicp);
  setIn("Pit Gain (bbl)",               pitGain);
  setIn("TVD (ft)",                     tvd);
  setIn("SCR (psi)",                    scrPressure);
  setIn("Kill Rate (spm)",              killRate);
  setIn("Pump Output (bbl/stk)",        pumpOutput);
  setIn("Drill String Vol (bbl)",       drillStringVol);
  setIn("Annular Vol (bbl)",            annularVol);
  setIn("Riser Vol (bbl)",              riserVol);
  setIn("Choke Line Vol (bbl)",         chokeLineVol);
  setIn("Kill Line Vol (bbl)",          killLineVol);
  setIn("Choke Line Friction (psi)",    chokeFriction);
  setIn("Kill Line Friction (psi)",     killFriction);
  setIn("Choke Friction Test Rate (spm)", chokeFrictionRate);
  setIn("Kill Friction Test Rate (spm)",  killFrictionRate);
  setIn("Casing Shoe TVD (ft)",         casingShoeTvd);
  setIn("Selected Volume (bbl)",        selectedVolume);
  setIn("LOT/FIT Notes",                lotFitNotes);
  setIn("Procedure Ref.",               procedureRef);
  setIn("User Notes",                   userNotes);

  const extras = {};
  const setOut = (k, v, unit) => { if (v !== null && v !== undefined) extras[k] = unit ? `${v} ${unit}` : v; };
  setOut("Kill Mud Weight",        kmw,                 "ppg");
  setOut("Subsea ICP Support",     icp,                 "psi");
  setOut("Subsea FCP Support",     fcp,                 "psi");
  setOut("Strokes to Bit",         strokesToBit,        "strokes");
  setOut("Bottoms-Up Strokes",     bottomsUp,           "strokes");
  setOut("Pump Rate (computed)",   pumpRateBblMin,      "bbl/min");
  setOut("Circulation Time",       circulationTimeMin,  "min");
  setOut("Riser Volume",           riserVolOut,         "bbl");
  setOut("Choke Line Volume",      chokeLineVolOut,     "bbl");
  setOut("Kill Line Volume",       killLineVolOut,      "bbl");
  extras["Stack Mode"] = "subsea";
  extras["Worksheet Type"] = "Subsea Kill Sheet Worksheet Support";
  extras["Status"] = _DRAFT_HEADER;
  if (missing.length > 0) extras["Missing data flags"] = missing.join("  |  ");
  extras["Warning"] = _KILL_WARNING_LINES.join("  ");

  // Headline value: prefer Kill MW (ppg); else 0 with an "incomplete" flag.
  const headline = kmw != null ? kmw : 0;
  const headlineUnit = kmw != null ? "ppg (Kill MW)" : "— (incomplete — see Missing data flags)";

  // Step-by-step summary (numerals always rounded)
  const steps = [];
  steps.push(_DRAFT_HEADER);
  if (kmw != null) {
    if (currentMW != null && sidpp != null && tvd != null) {
      steps.push(`Kill MW = ${currentMW} + ${sidpp} / (0.052 × ${tvd}) = ${kmw} ppg`);
    } else {
      steps.push(`Kill MW (user override) = ${kmw} ppg`);
    }
  }
  if (icp != null) steps.push(`Subsea ICP Support = ${sidpp} + ${scrPressure} + ${chokeFriction} = ${icp} psi`);
  if (fcp != null) steps.push(`Subsea FCP Support = ${scrPressure} × (${kmw} / ${currentMW}) + ${chokeFriction} = ${fcp} psi`);
  if (strokesToBit != null) steps.push(`Strokes to Bit = ${drillStringVol} / ${pumpOutput} = ${strokesToBit} strokes`);
  if (bottomsUp != null) steps.push(`Bottoms-Up Strokes = ${annularVol} / ${pumpOutput} = ${bottomsUp} strokes`);
  if (pumpRateBblMin != null) steps.push(`Pump Rate = ${pumpOutput} × ${killRate} = ${pumpRateBblMin} bbl/min`);
  if (circulationTimeMin != null) steps.push(`Circulation Time = ${selectedVolume} / ${pumpRateBblMin} = ${circulationTimeMin} min`);
  steps.push("Warnings: see Additional → Warning");

  return {
    result: headline,
    unit: headlineUnit,
    formula: "Subsea Kill Sheet Worksheet Support — multiple deterministic sub-calculations (see steps & Additional)",
    steps,
    inputs,
    extras,
    worksheetWarning: true,
  };
}


// =========================================================================
// STAGE 5 — Subsea BOP / Stack record-keeping tools (worksheets only).
// Each tool is intentionally documentation-only. The app must NOT certify
// equipment condition, test acceptance, regulatory compliance, or readiness.
// =========================================================================

const _BOP_RECORD_WARNING =
  "Subsea BOP and well-control equipment records are documentation-support tools only. They do not certify equipment condition, regulatory compliance, test acceptance, or operational readiness. Always follow manufacturer manuals, company procedures, regulatory requirements, and competent-person verification.";

// Read an optional non-empty string for the record tools.
function _recStr(v) {
  if (typeof v !== "string") return null;
  const t = v.trim();
  return t.length > 0 ? t : null;
}
function _recNum(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// Generic record-output builder used by all 4 BOP tools.
//   headline  : pair { result, unit } used for the calculator's headline answer
//   inputsMap : { label -> value }  (omits null/empty)
//   extras    : { label -> value }  (omits null/empty)
//   summary   : array of step strings
function _bopRecordOutput(headline, inputsMap, extras, summary) {
  const inputs = {};
  Object.entries(inputsMap).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== "") inputs[k] = v; });
  const ext = { ...extras };
  ext["Stack Mode"] = "subsea";
  ext["Status"] = "Record only — does NOT certify equipment, test acceptance, or readiness";
  ext["Warning"] = _BOP_RECORD_WARNING;
  return {
    result: headline.result,
    unit: headline.unit,
    formula: "Documentation record — no calculation performed",
    steps: [
      "DRAFT RECORD — documentation support only.",
      ...summary,
      "Reminder: certification of condition, test acceptance, and readiness is performed by competent persons under approved procedures — not by this app.",
    ],
    inputs,
    extras: ext,
    worksheetWarning: true,
  };
}

// 1) Subsea BOP Stack Configuration Record
export function subseaBopStackConfigurationRecord(args) {
  const rigName             = _recStr(args.rigName);
  const wellName            = _recStr(args.wellName);
  const stackType           = _recStr(args.stackType);
  const waterDepth          = _recNum(args.waterDepth);
  const annularDetails      = _recStr(args.annularDetails);
  const ramConfig           = _recStr(args.ramConfig);
  const shearRamDetails     = _recStr(args.shearRamDetails);
  const chokeLineDetails    = _recStr(args.chokeLineDetails);
  const killLineDetails     = _recStr(args.killLineDetails);
  const connectorDetails    = _recStr(args.connectorDetails);
  const controlPodDetails   = _recStr(args.controlPodDetails);
  const rovPanelNotes       = _recStr(args.rovPanelNotes);
  const acousticBackup      = _recStr(args.acousticBackup);
  const testPressureNotes   = _recStr(args.testPressureNotes);
  const lastFunctionDate    = _recStr(args.lastFunctionDate);
  const lastPressureDate    = _recStr(args.lastPressureDate);
  const notes               = _recStr(args.notes);

  const componentsCount = [annularDetails, ramConfig, shearRamDetails, connectorDetails, controlPodDetails]
    .filter(Boolean).length;

  return _bopRecordOutput(
    { result: componentsCount, unit: "stack components documented" },
    {
      "Rig": rigName, "Well": wellName, "Stack Type": stackType,
      "Water Depth (ft)": waterDepth,
      "Annular Preventer": annularDetails,
      "Ram Configuration": ramConfig,
      "Shear Ram": shearRamDetails,
      "Choke Line": chokeLineDetails,
      "Kill Line": killLineDetails,
      "Connector": connectorDetails,
      "Control Pods": controlPodDetails,
      "ROV Intervention Panel": rovPanelNotes,
      "Acoustic Backup": acousticBackup,
      "Test Pressure Notes": testPressureNotes,
      "Last Function Test": lastFunctionDate,
      "Last Pressure Test": lastPressureDate,
      "Notes": notes,
    },
    {
      "Record Type": "Subsea BOP Stack Configuration",
    },
    [
      "Subsea BOP Stack Configuration captured.",
      `Components documented: ${componentsCount}`,
    ],
  );
}

// 2) Subsea BOP Function Test Log — pass/fail is user-entered, never inferred.
export function subseaBopFunctionTestLog(args) {
  const dateTime         = _recStr(args.dateTime);
  const component        = _recStr(args.component);
  const functionTested   = _recStr(args.functionTested);
  const podUsed          = _recStr(args.podUsed);
  const responseTime     = _recNum(args.responseTime);   // seconds
  const pressureObserved = _recNum(args.pressureObserved); // psi
  const userResult       = _recStr(args.userResult);     // "Pass" / "Fail" / "N/A" — user only
  const witness          = _recStr(args.witness);
  const notes            = _recStr(args.notes);

  const headlineValue = responseTime != null ? responseTime : 0;
  const headlineUnit  = responseTime != null ? "s (response time — user-recorded)" : "—";

  return _bopRecordOutput(
    { result: headlineValue, unit: headlineUnit },
    {
      "Date/Time": dateTime,
      "Component": component,
      "Function Tested": functionTested,
      "Control System / Pod": podUsed,
      "Response Time (s)": responseTime,
      "Pressure Observed (psi)": pressureObserved,
      "Result (user-entered)": userResult,
      "Witness": witness,
      "Notes": notes,
    },
    {
      "Record Type": "Subsea BOP Function Test Log",
      "Note": "Pass/fail is recorded as entered by the user. The app does NOT independently certify this test.",
    },
    [
      `Component: ${component || "—"}  ·  Function: ${functionTested || "—"}`,
      `Pod: ${podUsed || "—"}  ·  Response time: ${responseTime != null ? responseTime + " s" : "—"}  ·  Pressure observed: ${pressureObserved != null ? pressureObserved + " psi" : "—"}`,
      `Result (user-entered): ${userResult || "—"}  ·  Witness: ${witness || "—"}`,
    ],
  );
}

// 3) Subsea BOP Pressure Test Record — pressure drop computed deterministically;
// acceptance and final result remain user-entered.
export function subseaBopPressureTestRecord(args) {
  const dateTime           = _recStr(args.dateTime);
  const componentTested    = _recStr(args.componentTested);
  const testPressure       = _recNum(args.testPressure);
  const holdTime           = _recNum(args.holdTime);          // minutes
  const startPressure      = _recNum(args.startPressure);
  const endPressure        = _recNum(args.endPressure);
  const userPressureDrop   = _recNum(args.pressureDrop);      // optional override
  const acceptance         = _recStr(args.acceptanceCriteria);
  const userResult         = _recStr(args.userResult);
  const witness            = _recStr(args.witness);
  const notes              = _recStr(args.notes);

  let computedDrop = null;
  if (startPressure != null && endPressure != null) {
    computedDrop = round(startPressure - endPressure, 2);
  }
  const drop = userPressureDrop != null ? userPressureDrop : computedDrop;
  const headlineValue = drop != null ? drop : 0;
  const headlineUnit  = drop != null ? "psi (pressure drop)" : "—";

  return _bopRecordOutput(
    { result: headlineValue, unit: headlineUnit },
    {
      "Date/Time": dateTime,
      "Component Tested": componentTested,
      "Test Pressure (psi)": testPressure,
      "Hold Time (min)": holdTime,
      "Starting Pressure (psi)": startPressure,
      "Ending Pressure (psi)": endPressure,
      "Pressure Drop (psi, user-entered)": userPressureDrop,
      "Acceptance Criteria (user-entered)": acceptance,
      "Result (user-entered)": userResult,
      "Witness": witness,
      "Notes": notes,
    },
    {
      "Record Type": "Subsea BOP Pressure Test",
      "Computed Pressure Drop (psi)": computedDrop != null ? computedDrop : "—",
      "Note": "Acceptance and pass/fail are recorded as entered by the user. The app does NOT independently certify this test.",
    },
    [
      `Component: ${componentTested || "—"}  ·  Test Pressure: ${testPressure != null ? testPressure + " psi" : "—"}  ·  Hold: ${holdTime != null ? holdTime + " min" : "—"}`,
      `Starting → Ending: ${startPressure != null ? startPressure : "—"} → ${endPressure != null ? endPressure : "—"} psi`,
      `Computed Drop: ${computedDrop != null ? computedDrop + " psi" : "—"}  ·  User-entered Drop: ${userPressureDrop != null ? userPressureDrop + " psi" : "—"}`,
      `Acceptance (user): ${acceptance || "—"}  ·  Result (user): ${userResult || "—"}  ·  Witness: ${witness || "—"}`,
    ],
  );
}

// 4) Pod / Control System Record
export function subseaPodControlRecord(args) {
  const activePod              = _recStr(args.activePod);
  const backupPod              = _recStr(args.backupPod);
  const functionTested         = _recStr(args.functionTested);
  const responseObserved       = _recStr(args.responseObserved);
  const hydraulicPressure      = _recNum(args.hydraulicPressure);
  const electricalStatus       = _recStr(args.electricalStatus);
  const rovBackupNotes         = _recStr(args.rovBackupNotes);
  const notes                  = _recStr(args.notes);

  const headlineValue = hydraulicPressure != null ? hydraulicPressure : 0;
  const headlineUnit  = hydraulicPressure != null ? "psi (hydraulic pressure)" : "—";

  return _bopRecordOutput(
    { result: headlineValue, unit: headlineUnit },
    {
      "Active Pod": activePod,
      "Backup Pod": backupPod,
      "Function Tested": functionTested,
      "Response Observed": responseObserved,
      "Hydraulic Pressure (psi)": hydraulicPressure,
      "Electrical / Communication": electricalStatus,
      "ROV Backup Notes": rovBackupNotes,
      "Notes": notes,
    },
    {
      "Record Type": "Subsea Pod / Control System",
      "Note": "Equipment readiness must be confirmed by competent persons under approved procedures.",
    },
    [
      `Active Pod: ${activePod || "—"}  ·  Backup Pod: ${backupPod || "—"}`,
      `Function tested: ${functionTested || "—"}  ·  Response: ${responseObserved || "—"}`,
      `Hydraulic Pressure: ${hydraulicPressure != null ? hydraulicPressure + " psi" : "—"}  ·  Electrical/Comm: ${electricalStatus || "—"}`,
      `ROV Backup: ${rovBackupNotes || "—"}`,
    ],
  );
}

